import { ISolicitation } from '../../models/solicitation-models/solicitation/SolicitationModel';
import { RequestSolicitationsService } from '../solicitations/request-solicitations.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TokenService } from '../security/token.service';
import { LinkupService } from '../link-up/linkup.service';
import { CatalogService } from '../catalogs/catalog.service';
import { ApplicationService } from '../application/application.service';
import { findInArray, getValueProperty, normalizeCryptoVaultSettings, recaptchaHeaders, removeAccents, setValueProperty } from '../../helpers/functions.helpers';
import { lastValueFrom } from '../../helpers/functions.helpers';
import { UnicodePipe } from '../../pipe/unicode.pipe';
import { retry } from 'rxjs/operators';

import moment from "moment";

export interface IFieldParameterRequest {
  client: {
    person_type: string
  },
  document: {
    type: string,
    number: string,
    type_id: string | number
  },
  solicitation: {
    solicitation_id: string | number,
    client_name: string,
    solicitation_id_pqr: string | number,
    validate_mimetype: string,
    solicitation_application: string
  },
  additional_information: {
    key: string
  }
}

export interface IRequestFile {
  dependence_id: string,
  document_type: string,
  process_name: string,
  file_id: string,
  file: File,
  file_extension: string,
  field_parameter?: IFieldParameterRequest | null,
  field_parameter_crypto?: {
    field_parameter?: string,
    fields?: Array<string>,
    headers?: Array<string>
  }
}

@Injectable({
  providedIn: 'root'
})
export class FileService {

  constructor(
    private _http: HttpClient,
    private _token: TokenService,
    private _link: LinkupService,
    private _solicitation: RequestSolicitationsService,
    private _catalog: CatalogService,
    private readonly _app: ApplicationService
  ) { }

  /**
   * Permite realizar el envio de los archivos suministrados por el usuario
   * @returns
   */
  public async send(data: IRequestFile | Array<IRequestFile>, component = 'upload-files') {
    if (Array.isArray(data)) {
      for (let file of data) {
        await this.filePost(file, component);
      }
    } else {
      await this.filePost(data, component);
    }
  }

  /**
   * Realiza el envio de cada archivo cargado por el usuario
   * @param data
   * @returns
   */
  private async filePost(data: IRequestFile, component = 'upload-files') {
    const settings = await this._link.settings;
    const contextUrls = await this._link.contextUrls;
    const useCryptoVault = this._app.getSetting(getValueProperty(settings, `context.settings.${component}`), "services", "useCryptoVault", false);
    const URL = `${contextUrls.URLS.ONBASE_ARCHIVES}`;
    let httpOptions: any = await this.httpOptions();

    if(this._app.useRecaptcha && getValueProperty(settings, `request.ONBASE_ARCHIVES.recaptcha`, false)){
      const recaptchaSettings = getValueProperty(settings, `request.ONBASE_ARCHIVES.recaptcha`, { options: {}, headers: {} });
      const captchaOptions = getValueProperty(recaptchaSettings, "options", {"action": `${component}`});
      httpOptions = await recaptchaHeaders(this._app, httpOptions, recaptchaSettings, captchaOptions);
    }

    if (useCryptoVault) {
      const transaction_id = await this._token.transactionId;
      await this._app.initCryptoVault(transaction_id);
      const cryptoVaultSettings = getValueProperty(settings, 'request.ONBASE_ARCHIVES.cryptoVault', { fields: [], headers: [] });
      data = await this._app.encrypt(data, getValueProperty(cryptoVaultSettings, "fields", []));
      httpOptions.headers = new HttpHeaders(await this._app.encrypt(httpOptions.headers, getValueProperty(cryptoVaultSettings, "headers", []), true));
      data.field_parameter_crypto = { field_parameter: `${data.field_parameter}`, ...normalizeCryptoVaultSettings(cryptoVaultSettings) };
      data.field_parameter = null;
    }

    const body = new FormData();
    body.append("dependence_id", data.dependence_id);
    body.append("document_type", data.document_type);
    body.append("process_name", data.process_name);
    body.append("file_id", data.file_id);
    body.append("file", data.file);
    body.append("field_parameter", new Blob([
      JSON.stringify(data.field_parameter)
    ],
      { type: "application/json" }
    ));

    if (data.field_parameter_crypto) {
      body.append("field_parameter_crypto", new Blob([
        JSON.stringify(data.field_parameter_crypto)
      ],
        { type: "application/json" }
      ));
    }

    let req$ = this._http.post(URL, body, httpOptions)
      .pipe(retry(getValueProperty(settings, `request.ONBASE_ARCHIVES.OPTIONS.retry`, 0)));
    return await lastValueFrom(req$);
  }

  /**
   * Permite construir de manera dinamica los encabezados para las peticiones al servicio
   */
  private async httpOptions() {
    const { accessToken } = await this._token.accessToken;
    const transaction_id = await this._token.transactionId;
    const session_id = await this._token.sessionId;

    return {
      headers: new HttpHeaders()
        .set('Authorization', `Bearer ${accessToken}`)
        .set('token-user-id', `pj_u${getValueProperty(this._token, 'userData.document_number', '0')}_${getValueProperty(this._token, 'userData.document_number', '0')}`)
        .set('transaction_id', transaction_id)
        .set('application', this._app.application)
        .set('channel-id', this._app.channelId)
        .set('session_id', session_id)
        .set('terminal_id', '127.0.0.1')
        .set('timestamp', new Date().toISOString())
    };

  }

  /**
   * Permite construir el objeto que sera envia al servicio de prefirmado
   * @param data
   * @returns
   */
  async buildData(data: ISolicitation): Promise<Array<IRequestFile>> {

    const FILE_SETTINGS = getValueProperty(await this._link.settings, 'onbase.OPTIONS.data', {});
    const filesUpload = getValueProperty(data, 'additionalData.additional_data', []);

    let buildData: Array<IRequestFile> = [];
    let indexFileProcess = 0;

    const dependenceId = getValueProperty(FILE_SETTINGS, 'dependence_id');
    const processName = getValueProperty(FILE_SETTINGS, 'process_name');

    let client = this._solicitation.solicitation.basicData;
    let additionals = this._solicitation.solicitation.additionalData;
    let catalogDocuments = await this._catalog.documentTypes;
    let catalogType = await this._catalog.customerTypes;
    let customer = findInArray(catalogType, 'id', getValueProperty(additionals, 'CustomerTypes'));

    const documentTypeId = getValueProperty(client, 'document_type');
    const documentTypeData = findInArray(catalogDocuments, 'id', documentTypeId);

    const documentTypeParameters = getValueProperty(documentTypeData, 'parameters', []);
    const codeOnBaseParameter = findInArray(documentTypeParameters, 'key', 'code_onbase');
    const codeParameter = findInArray(documentTypeParameters, 'key', 'code');

    /* 
    * Se realiza validación de parametro homologo para onbase en 
    * caso de no encontrarse el valor se envia la homologacion 
    * del codigo para sqr y si no existe ninguno de los dos se 
    * envia el identificador del tipo de documento ej. 1
    */
    let document = getValueProperty(codeOnBaseParameter, 'value', getValueProperty(codeParameter, 'value', documentTypeId));
    
    //let unicode = new UnicodePipe();
    //let documentType = unicode.transform(getValueProperty(documentTypeData, 'name', ''));
    //let document = removeAccents(`${documentType}`);

    let client_names = getValueProperty(client, 'client_names', '').trim();
    let client_surnames = getValueProperty(client, 'client_surnames', '').trim();
    if (client_names !== client_surnames) {
      client_names += " " + client_surnames;
    }
    client_names = client_names.substring(0, 79);

    let fieldParameter: IFieldParameterRequest = {
      client: {
        person_type: getValueProperty(customer, 'onbase')
      },
      document: {
        type: document,
        type_id: documentTypeId,
        number: getValueProperty(client, 'document_number')
      },
      solicitation: {
        solicitation_id: this._solicitation.transactionCode,
        client_name: client_names,
        solicitation_id_pqr: this._solicitation.solicitationId,
        validate_mimetype: 'true',
        solicitation_application: this._app.currentApplication
      },
      additional_information: {
        key: getValueProperty(additionals, 'Cell.name', ''),
      }
    };

    let time = moment().unix();
    for (let fileItem of filesUpload) {
      const fileId = `${this._solicitation.transactionCode}_${time}_${++indexFileProcess}`;
      let fileData: IRequestFile = {
        dependence_id: dependenceId,
        document_type: getValueProperty(fileItem, 'settings.id', 0),
        process_name: processName,
        file_id: fileId,
        file: fileItem.file,
        file_extension: getValueProperty(fileItem, 'settings.type', ""),
        field_parameter: fieldParameter,
      };
      buildData.push(fileData);
    }
    return buildData;
  }

}
